import React from 'react';
import { useHistory } from 'react-router-dom';

import * as ManifestUI from '@project44/manifest-ds';
import { AxiosDataState, DataState, User } from '@project44/ui-utils';
import {
  DtoBase,
  Enums,
  TenantEnums,
  TenantModels,
  UIDtoFactory,
} from '@project44-models/iam-gateway';

import { ReactComponent } from '../../../../../shared/abstract/infrastructrue/ReactComponent';
import { ComponentPropsValueObject } from '../../../../../shared/core/domain/ComponentPropsValueObject';
import { TAnyOfPermissionsHOC } from '../../../../../shared/core/infrastructrue/types';
import { TUseSwitchTenantHook } from '../../../domain';
import { TenantTileCardComponent } from '../TenantTileCard/TenantTileCard.component';
import { TenantSwitcherView, TTenantSwitcher } from './TenantSwitcher.view';

type TRenderResolver = {
  assertionCollection: Array<boolean>;
  renderer: React.FC;
};

export class TenantSwitcherComponent extends ReactComponent {
  public static redirectTMUIPathName: string = '/tenant-management';

  public async buildTenantQueryDto(
    searchQuery: string,
    tenantQueryHookMutator: DataState<
      DtoBase<TenantModels.TenantQueryResultsViewModel>
    >['mutate'],
  ): Promise<DtoBase<TenantModels.TenantQueryViewModel> | undefined> {
    const { UiDtoFactoryEnum } = Enums;

    const queryDto = UIDtoFactory.create(UiDtoFactoryEnum.TENANT_QUERY, {
      direction: TenantEnums.PaginationSortEnum.ASC,
      limit: 5,
      operator: TenantEnums.TdxSearchOperatorEnum.LIKE,
      page: 1,
      property: TenantEnums.FilterTenantPropertiesEnum.ALL,
      query: searchQuery,
      sortBy: TenantEnums.SortableAndSearchableTenantModelPropertiesEnum.NAME,
      status: TenantEnums.TenantStatusEnum.ACTIVE_STATUS,
    });

    if (!!searchQuery.length) {
      return queryDto;
    }

    await tenantQueryHookMutator(
      () =>
        UIDtoFactory.create(Enums.UiDtoFactoryEnum.TENANT_QUERY_RESULTS, {}),
      false,
    );

    return undefined;
  }

  public handleSwitchTenantState(payload: {
    switchTenantState: AxiosDataState<unknown>;
  }): void {
    const { switchTenantState } = payload;

    switch (true) {
      case switchTenantState.data !== undefined:
        window.location.reload();
        return;
      case switchTenantState.error !== undefined:
        ManifestUI.toast.error('Error', {
          description: switchTenantState?.error?.message,
          onDismiss() {
            switchTenantState.resetState();
          },
        });
        return;
      default:
        return;
    }
  }

  public handleComposeRenderedElements(
    payload: {
      tenantQueryResultsDataState: DataState<
        DtoBase<TenantModels.TenantQueryResultsViewModel>
      >;
      searchQuery: string;
      user?: User;
      switchTenant: ReturnType<TUseSwitchTenantHook>[1];
    },
    UserPrincipalCurrentTenantCardComponent: React.ReactElement,
  ): TRenderResolver | undefined {
    const {
      searchQuery,
      user,
      switchTenant,
      tenantQueryResultsDataState: { isLoading, error, data },
    } = payload;

    const renderResolverCollection: Array<TRenderResolver> = [
      this.handleRenderLoading({ isLoading, user }),
      this.handleRenderError({ error }),
      this.handleRenderData({
        data,
        isLoading,
        switchTenant,
        searchQuery,
        user,
      }),
      this.handleRenderDefaultView(
        { data, searchQuery },
        UserPrincipalCurrentTenantCardComponent,
      ),
      this.handleRenderEmptyData({ data, isLoading, searchQuery }),
    ];

    return renderResolverCollection.find((resolver) => {
      if (resolver.assertionCollection.every(Boolean)) {
        return resolver;
      }
    });
  }

  public handleTenantQueryResultsDataState(payload: {
    switchTenantAxiosDataState: AxiosDataState;
    tenantQueryResultsDataState: DataState<
      DtoBase<TenantModels.TenantQueryResultsViewModel>
    >;
  }): DataState<DtoBase<TenantModels.TenantQueryResultsViewModel>> {
    const {
      switchTenantAxiosDataState,
      tenantQueryResultsDataState: { isLoading, mutate, data, error },
    } = payload;

    const shouldDisplayTenantQueryResults =
      switchTenantAxiosDataState.isLoading ||
      switchTenantAxiosDataState.data !== undefined;

    return {
      error: error || switchTenantAxiosDataState.error,
      isLoading: isLoading || switchTenantAxiosDataState.isLoading,
      data: shouldDisplayTenantQueryResults ? undefined : data,
      mutate,
    };
  }

  public handleRenderTenantManagementUILink(payload: {
    toggleTenantSwitcherComponent?: () => void;
    tenantQueryResultsDataState: DataState<
      DtoBase<TenantModels.TenantQueryResultsViewModel>
    >;
    locationPathName: string;
    searchQuery: string;
    pushHistoryFn: ReturnType<typeof useHistory>['push'];
  }): React.ReactElement | null {
    const { spaceSmall } = ManifestUI.designTokens;
    const { redirectTMUIPathName } = TenantSwitcherComponent;
    const {
      tenantQueryResultsDataState: { isLoading, data },
      pushHistoryFn,
      toggleTenantSwitcherComponent,
      searchQuery,
      locationPathName,
    } = payload;

    const shouldDisplayTMUILink = [
      !!searchQuery.length &&
        !isLoading &&
        Number(data?.source?.getTotal()) > 5,
    ];

    const handleOnClickRedirect = () => {
      toggleTenantSwitcherComponent?.();

      if (redirectTMUIPathName !== locationPathName) {
        const timer = setTimeout(() => {
          clearTimeout(timer);
          pushHistoryFn({ pathname: '/tenant-management' });
        }, 250);
      }
    };

    return shouldDisplayTMUILink.includes(true) ? (
      <ManifestUI.Typography
        aria-label={'Search in Tenant Management UI'}
        role={'presentation'}
        variant={'caption'}
        css={{
          cursor: 'pointer',
          padding: `${spaceSmall} 0`,
        }}
      >
        <ManifestUI.Link
          css={{ fontSize: 'inherit' }}
          onClick={handleOnClickRedirect}
        >
          Search in Tenant Management UI
        </ManifestUI.Link>
      </ManifestUI.Typography>
    ) : null;
  }

  public static create({
    props,
  }: ComponentPropsValueObject<
    TAnyOfPermissionsHOC<Omit<TTenantSwitcher, 'tenantSwitcherComponent'>>
  >): React.ReactElement<TTenantSwitcher> {
    const tenantSwitcherProps: TTenantSwitcher = {
      ...props,
      tenantSwitcherComponent: new TenantSwitcherComponent(),
    };

    const withIntersectionObserver = this.composeWithIntersectionObserver(
      TenantSwitcherView,
      {
        delay: 100,
        initialInView: false,
        rootMargin: '20px -400px',
        threshold: 0,
        trackVisibility: true,
      },
    );

    const withManifestUIToaster = this.composeWithManifestUIToaster(
      withIntersectionObserver,
    );

    return this.withAnyOfPermissionsHOC(
      ['system-admin:auth:switch-to-any-tenant'],
      withManifestUIToaster,
      tenantSwitcherProps,
    );
  }

  private handleRenderLoading(
    payload: Pick<DataState<unknown>, 'isLoading'> & { user: User | undefined },
  ): TRenderResolver {
    return {
      assertionCollection: [payload.isLoading],
      renderer: () => {
        return payload.user === undefined
          ? null
          : TenantTileCardComponent.create({
              user: payload.user,
              isLoading: true,
              tenantDto: UIDtoFactory.create(Enums.UiDtoFactoryEnum.TENANT, {}),
            });
      },
    };
  }

  private handleRenderError(
    payload: Pick<DataState<unknown>, 'error'>,
  ): TRenderResolver {
    const { error } = payload;

    return {
      assertionCollection: [!!error],
      renderer: () => {
        return (
          <ManifestUI.Box
            css={{
              margin: ManifestUI.designTokens.spaceLarge,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <ManifestUI.Typography variant={'subtextBold'}>
              Error
            </ManifestUI.Typography>
            <ManifestUI.Typography variant={'subtext'}>
              {error?.message}
            </ManifestUI.Typography>
          </ManifestUI.Box>
        );
      },
    };
  }

  private handleRenderData(
    payload: Pick<
      DataState<DtoBase<TenantModels.TenantQueryResultsViewModel>>,
      'data' | 'isLoading'
    > & {
      user: User | undefined;
      searchQuery: string;
      switchTenant: ReturnType<TUseSwitchTenantHook>[1];
    },
  ): TRenderResolver {
    const { user, data, searchQuery, switchTenant } = payload;

    return {
      assertionCollection: [
        !!payload.data,
        !!payload.data?.source.getData().length,
        !payload.isLoading,
        !!payload.searchQuery.length,
      ],
      renderer: () => {
        const handleSwitchTenantOnClick = async (payload: {
          tenantDto: DtoBase<TenantModels.TenantViewModel>;
        }) => {
          await switchTenant({ tenantDto: payload.tenantDto });
        };

        return data === undefined || user === undefined ? null : (
          <React.Fragment>
            {data.source.getData().map((tenantModel) => {
              return TenantTileCardComponent.create({
                user: user,
                isLoading: false,
                key: tenantModel.tenantUuid,
                handleOnClick: handleSwitchTenantOnClick,
                tenantDto: UIDtoFactory.create(
                  Enums.UiDtoFactoryEnum.TENANT,
                  tenantModel,
                ),
                highlightableConfig: {
                  searchQuery,
                },
              });
            })}
          </React.Fragment>
        );
      },
    };
  }

  private handleRenderEmptyData(
    payload: Pick<
      DataState<DtoBase<TenantModels.TenantQueryResultsViewModel>>,
      'data' | 'isLoading'
    > & { searchQuery: string },
  ): TRenderResolver {
    const { isLoading, data, searchQuery } = payload;

    const isEmptySearchResults =
      !!data &&
      !data.source.getData().length &&
      !!searchQuery.length &&
      !isLoading;

    const isSearchingReturnedResults =
      !!data &&
      !!data.source.getData().length &&
      !searchQuery.length &&
      !isLoading;

    return {
      assertionCollection: [isSearchingReturnedResults || isEmptySearchResults],
      renderer: () => (
        <ManifestUI.Box
          css={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            padding: ManifestUI.designTokens.spaceLarge,
            width: '100%',
          }}
        >
          <ManifestUI.Typography variant={'subtextBold'}>
            No tenants found
          </ManifestUI.Typography>
          {!!searchQuery.length && (
            <ManifestUI.Typography
              css={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                marginTop: ManifestUI.designTokens.spaceSmall,
              }}
              variant={'subtext'}
            >
              We couldn’t find a tenant matching <strong>{searchQuery}</strong>
            </ManifestUI.Typography>
          )}
        </ManifestUI.Box>
      ),
    };
  }

  private handleRenderDefaultView(
    payload: Pick<
      DataState<DtoBase<TenantModels.TenantQueryResultsViewModel>>,
      'data'
    > & { searchQuery: string },
    UserPrincipalCurrentTenantCardComponent: React.ReactElement,
  ): TRenderResolver {
    const { data, searchQuery } = payload;

    const isDefaultView =
      !!data && !data.source.getData().length && !searchQuery.length;

    return {
      assertionCollection: [data === undefined || isDefaultView],
      renderer: () => {
        return UserPrincipalCurrentTenantCardComponent;
      },
    };
  }
}
